<template>
  <el-popover
    placement="bottom"
    width="300"
    trigger="hover"
    popper-class="points-popover-panel"
    @show="handlePopoverShow"
  >
    <div class="points-panel">
      <section class="points-panel__summary">
        <p class="points-panel__label">
          {{ $t("header.pointsPopover.currentPoints") }}
        </p>
        <p class="points-panel__total">{{ formatNumber(totalPoints) }}</p>
      </section>
      <template v-if="canShowTeamPoints">
        <div class="points-panel__divider"></div>

        <section class="points-panel__team">
          <div>
            <p class="points-panel__team-label">
              {{ $t("header.pointsPopover.teamTotalPoints") }}
            </p>
            <p class="points-panel__team-value">
              {{ formatNumber(allocatablePoints) }}
            </p>
          </div>
          <button
            type="button"
            class="points-panel__team-action"
            @click="handleTeamManagement"
          >
            {{ $t("header.pointsPopover.allocatePoints") }}
          </button>
        </section>
      </template>
      <div class="points-panel__divider"></div>

      <section class="points-panel__breakdown">
        <div class="points-panel__row">
          <div class="points-panel__row-left">
            <span class="dot dot--gray"></span>
            <span class="points-panel__row-label">
              {{ $t("header.pointsPopover.rechargePoints") }}
            </span>
          </div>
          <span class="points-panel__row-value">{{
            formatNumber(directPoints)
          }}</span>
        </div>
        <div v-if="!subscriptionButtonHidden" class="points-panel__row">
          <div class="points-panel__row-left">
            <span class="dot dot--gold"></span>
            <span class="points-panel__row-label">
              {{ $t("header.pointsPopover.subscriptionPoints") }}
            </span>
          </div>
          <span class="points-panel__row-value points-panel__row-value--gold">
            {{ formatNumber(subscriptionPoints) }}
          </span>
        </div>
      </section>

      <div
        class="points-panel__divider points-panel__divider--footer-top"
      ></div>

      <section class="points-panel__footer">
        <button
          type="button"
          class="points-panel__action points-panel__action--membership"
          @click="handleMembership"
          v-if="canShowMembershipPurchase"
        >
          <span class="points-panel__action-icon" aria-hidden="true">💎</span>
          <span>{{ $t("header.pointsPopover.buyMembership") }}</span>
        </button>

        <button
          type="button"
          class="points-panel__action points-panel__action--membership"
          @click="handleRecharge"
          v-if="!this.$store.state.isCloseVip"
        >
          <i class="el-icon-star-on points-panel__action-icon"></i>
          <span>{{ $t("header.pointsPopover.buyPoints") }}</span>
        </button>

        <div class="points-panel__divider"></div>

        <button
          type="button"
          class="points-panel__usage-link"
          @click="handleViewUsage"
        >
          <span>{{ $t("header.pointsPopover.viewUsage") }}</span>
          <i class="el-icon-arrow-right points-panel__arrow"></i>
        </button>
      </section>
    </div>

    <div class="points-trigger" slot="reference">
      <button type="button" class="credits-btn-bar">
        <span class="icon">★</span>
        <span>{{ $t("header.pointsPopover.managePoints") }}</span>
        <span class="amount">{{ formatNumber(totalPoints) }}</span>
        <span class="arrow">▼</span>
      </button>
      <!-- <span>{{ "积分：" + formatNumber(totalPoints) }}</span>
      <span class="points-trigger__sep">|</span>
      <span class="points-trigger__recharge" @click.stop="handleRecharge">充值</span> -->
    </div>
  </el-popover>
</template>

<script>
import bus from "@/bus.js";
import {
  CLIENT_TYPE,
  isApiSuccess,
  isSubscriptionButtonHidden,
  mapUserSubscriptionInfo,
} from "@/utils/subscription";
import { canPurchaseMembership, isManager } from "@/utils/accountRole";

export default {
  name: "PointsPopover",
  data() {
    return {
      totalPoints: 0,
      subscriptionPoints: 0,
      directPoints: 0,
      // 当前管理员本人可分配直充/订阅积分（来自团队总览，不再用客户积分池）
      allocatableRecharge: 0,
      allocatableSubscription: 0,
      user: {},
    };
  },
  computed: {
    // 超管、一级、二级展示本人可分配积分入口
    subscriptionButtonHidden() {
      return isSubscriptionButtonHidden(this.user);
    },
    canShowTeamPoints() {
      return (
        Number(this.$store.state.userClientType) === CLIENT_TYPE.ENTERPRISE &&
        isManager(this.user)
      );
    },
    allocatablePoints() {
      if (this.subscriptionButtonHidden) {
        return Number(this.allocatableRecharge || 0);
      }
      return (
        Number(this.allocatableRecharge || 0) +
        Number(this.allocatableSubscription || 0)
      );
    },
    // 购买会员入口：企业仅超级管理员；个人客户可购买
    canShowMembershipPurchase() {
      return (
        !this.$store.state.isCloseVip &&
        !this.subscriptionButtonHidden &&
        canPurchaseMembership(this.user)
      );
    },
  },
  mounted() {
    this.syncUserFromStorage();
    this.refreshPointsData();
    bus.$on("earnPoints", this.refreshPointsData);
  },
  beforeDestroy() {
    bus.$off("earnPoints", this.refreshPointsData);
  },
  methods: {
    syncUserFromStorage() {
      try {
        this.user = JSON.parse(window.localStorage.getItem("user")) || {};
      } catch (e) {
        this.user = {};
      }
    },
    handlePopoverShow() {
      this.syncUserFromStorage();
      this.refreshPointsData();
    },
    refreshPointsData() {
      this.fetchPersonalInfo();
      if (this.canShowTeamPoints) {
        this.fetchTeamOverview();
      }
    },
    fetchPersonalInfo() {
      this.$axios
        .get(this.$store.state.serverUrl + "ytb/subscription/my/info")
        .then((res) => {
          if (!isApiSuccess(res.data.code)) {
            return;
          }
          const data = res.data.data || {};
          this.totalPoints = Number(data.totalPoints || 0);
          this.subscriptionPoints = Number(data.vipIntegral || 0);
          this.directPoints = Number(data.topUpIntegral || 0);

          const subscriptionInfo = mapUserSubscriptionInfo(data);
          this.$store.commit("setUserSubscriptionInfo", {
            ...subscriptionInfo,
            vipType: subscriptionInfo.subscribed ? 1 : 0,
          });

          this.$emit("points-updated", {
            totalPoints: this.totalPoints,
            subscriptionPoints: this.subscriptionPoints,
            directPoints: this.directPoints,
            subscribed: Boolean(data.subscribed),
          });
        })
        .catch(() => {});
    },
    // 可分配余额取当前管理员本人账户，不读客户积分池
    fetchTeamOverview() {
      this.$axios
        .get(this.$store.state.serverUrl + "ytb/user/client/team/overview")
        .then((res) => {
          if (!isApiSuccess(res.data.code)) {
            return;
          }
          const data = res.data.data || {};
          this.allocatableRecharge = Number(data.rechargePool || 0);
          this.allocatableSubscription = Number(data.subscriptionPool || 0);
        })
        .catch(() => {});
    },
    formatNumber(value) {
      const locale = this.$i18n.locale === "yue" ? "zh-HK" : this.$i18n.locale;
      return Number(value || 0).toLocaleString(locale);
    },
    handleRecharge() {
      this.$emit("recharge");
    },
    handleMembership() {
      this.$emit("membership");
    },
    handleViewUsage() {
      this.$emit("view-usage");
    },
    handleTeamManagement() {
      this.$router.push("/teamManagement");
    },
  },
};
</script>

<style lang="less">
.points-popover-panel {
  padding: 0 !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;

  &[x-placement^="bottom"] .popper__arrow {
    border-bottom-color: rgba(51, 56, 78, 0.85) !important;

    &::after {
      border-bottom-color: #11121b !important;
    }
  }
}
</style>

<style lang="less" scoped>
.points-panel {
  padding: 18px 20px 16px;
  border-radius: 12px;
  background: #11121b;
  border: 1px solid rgba(51, 56, 78, 0.85);
  color: #f8fafc;
}

.points-panel__summary {
  padding-bottom: 2px;
}

.points-panel__label {
  margin: 0 0 8px;
  color: #8b93a7;
  font-size: 13px;
  line-height: 1.2;
}

.points-panel__total {
  margin: 0;
  color: #ffffff;
  font-size: 34px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0.02em;
}

.points-panel__divider {
  height: 1px;
  margin: 16px 0;
  background: rgba(255, 255, 255, 0.06);

  &--footer-top {
    margin: 12px 0 0;
  }
}

.points-panel__breakdown {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.points-panel__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.points-panel__row-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;

  &--gray {
    background: #64748b;
  }

  &--gold {
    background: #ffc400;
  }
}

.points-panel__row-label {
  color: #d8deea;
  font-size: 14px;
  line-height: 1.2;
}

.points-panel__row-value {
  flex-shrink: 0;
  color: #f8fafc;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;

  &--gold {
    color: #ffc400;
  }
}

.points-panel__team {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.points-panel__team-label {
  margin: 0 0 6px;
  color: #8b93a7;
  font-size: 13px;
  line-height: 1.2;
}

.points-panel__team-value {
  margin: 0;
  color: #a78bfa;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
}

.points-panel__team-action {
  flex-shrink: 0;
  min-width: 64px;
  height: 28px;
  padding: 0 12px;
  border: none;
  border-radius: 6px;
  background: linear-gradient(135deg, #a855f7, #6366f1);
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 6px 18px rgba(99, 102, 241, 0.45);
  }
}

.points-panel__footer {
  padding-top: 14px;
}

.points-panel__footer .points-panel__divider {
  margin: 14px 0 0;
}

.points-panel__action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-sizing: border-box;
  width: 100%;
  min-height: 44px;
  margin-bottom: 10px;
  padding: 0 16px;
  border-radius: 8px;
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
  transition: filter 0.2s, box-shadow 0.2s;

  &:hover,
  &:focus-visible {
    filter: brightness(1.08);
  }

  &:focus-visible {
    outline: 2px solid #c4b5fd;
    outline-offset: 2px;
  }

  &--membership {
    border: 1px solid transparent;
    background: linear-gradient(135deg, #a855f7, #6366f1);
    box-shadow: 0 6px 18px rgba(99, 102, 241, 0.32);
  }
}

.points-panel__action-icon {
  font-size: 20px;
}

.points-panel__usage-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 0 0;
  border: none;
  background: transparent;
  color: #a78bfa;
  font-size: 13px;
  line-height: 1.2;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: #c4b5fd;
  }

  &:focus-visible {
    outline: 2px solid #a78bfa;
    outline-offset: 2px;
  }
}

.points-panel__arrow {
  font-size: 12px;
  opacity: 0.65;
  transition: opacity 0.2s;
}

// .points-trigger {
// width: 168px;
// height: 32px;
// background: linear-gradient(135deg, #a855f7, #6366f1);
// box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
// border-radius: 30px;
// color: #ffffff;
// display: flex;
// align-items: center;
// justify-content: space-around;
// margin-right: 46px;
// cursor: pointer;
// transition: all 0.2s;

// &:hover {
//   box-shadow: 0 6px 20px rgba(99, 102, 241, 0.5);
// }
// }

.credits-btn-bar {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  height: 32px;
  gap: 6px;
  padding: 0 14px;
  background: #15152a;
  border: 1px solid #1e1e35;
  border-radius: 20px;
  font-size: ~"max(11PX, 0.85714rem)";
  color: #e8e6f0;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
}
.credits-btn-bar .arrow {
  font-size: 9px;
  color: #5c5b6e;
  margin-left: 2px;
  line-height: 1;
}
.credits-btn-bar .icon {
  color: #f0b90b;
  font-size: 12px;
  line-height: 1;
}
.credits-btn-bar .amount {
  font-size: ~"max(11PX, 0.92857rem)";
  line-height: 1;
}
.points-trigger__sep {
  opacity: 0.85;
}

.points-trigger__recharge {
  cursor: pointer;
}
</style>
